import { defineStore } from "pinia"
import { ref } from "vue"

type devUserType = {
  email: string
  password: string
}

type urlsType = {
  app: string // env.urls.app
  login: string // env.urls.login
  sso_login: string // env.urls.sso_login
  api: string // env.urls.api
  i18n: string // env.urls.i18n
  assets: string // env.urls.assets
  tests: string // env.urls.tests
  cdn: string // env.urls.cdn
  cdn_legacy: string // env.urls.cdn_legacy
  personalAccount: string // env.urls.personalAccount
  recruiterAccount: string // env.urls.recruiterAccount
  managerAccount: string // env.urls.managerAccount
  adminAccount: string // env.urls.adminAccount
  auth_url: string // env.urls.auth_url
  avatars_url: string // env.urls.avatars_url
  integration_url: string // env.urls.integration_url
  integration_url_v1: string // env.urls.integration_url_v1
  status_url: string // env.urls.status_url
  livekit_server_url: string // env.urls.livekit_server_url
  cookie_domain: string // env.urls.cookie_domain
}

type pusherType = {
  key: string
  cluster: string
}
type ChatbotSupport = {
  key: string
  url: string
}

type State = {
  appReady: boolean
  urls: urlsType
  mixpanel: "test" | "production" | ""
  dev_user: devUserType | null
  mockMode: "record" | "read" | "none"
  testMode: boolean
  oauth_token: string
  postMessage: string | null
  environment: "production" | "staging" | "development" | "preprod"
  pusher: pusherType
  chatbot_support: ChatbotSupport
}

const DEFAULT_PUSHER: pusherType = {
  key: "",
  cluster: "",
}

const DEFAULT_URL = {
  app: "default.app",
  login: "default.login",
  sso_login: "default.sso_login",
  api: "default.api",
  i18n: "default.i18n",
  assets: "default.assets",
  tests: "default.tests",
  cdn: "default.cdn",
  cdn_legacy: "default.cdn_legacy",
  personalAccount: "default.pa",
  recruiterAccount: "default.recruiter_account",
  managerAccount: "default.manager_account",
  adminAccount: "default.admin_account",
  auth_url: "default.auth_url",
  avatars_url: "default.avatars_url",
  integration_url: "default.integration_url",
  integration_url_v1: "default.integration_url_v1",
  status_url: "default.status_url",
  livekit_server_url: "default.livekit_server_url",
  cookie_domain: "default.cookie_domain",
}

export const useConfigStore = defineStore("config", () => {
  const appReady = ref(false)
  const urls = ref<urlsType>(DEFAULT_URL)
  const mixpanel = ref<"test" | "production" | "">("")
  const dev_user = ref<devUserType | null>(null)
  const mockMode = ref<"record" | "read" | "none">("none")
  const testMode = ref(false)
  const oauth_token = ref("")
  const postMessage = ref<string | null>(null)
  const environment = ref<string | null>(null)
  const pusher = ref<pusherType>(DEFAULT_PUSHER)
  const chatbotSupport = ref<ChatbotSupport>()

  function setConfig(config: State) {
    mixpanel.value = config.mixpanel
    urls.value = config.urls
    dev_user.value = config.dev_user
    mockMode.value = config.mockMode
    testMode.value = config.testMode
    environment.value = config.environment
    oauth_token.value = config.oauth_token
    pusher.value = config.pusher
    chatbotSupport.value = config.chatbot_support
  }

  function setPostMessage(value: State["postMessage"]) {
    postMessage.value = value
  }

  // when all initial loads are done, set appReady to true
  function setAppIsReady() {
    appReady.value = true
  }

  return {
    appReady,
    mixpanel,
    urls,
    dev_user,
    mockMode,
    testMode,
    environment,
    oauth_token,
    chatbotSupport,
    postMessage,
    pusher,
    setConfig,
    setPostMessage,
    setAppIsReady,
  }
})
